import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	FileInput,
	Loading,
	InputControlled,
	SelectControlled,
} from 'components';
import { NewPatientStep } from './NewPatientStep';
import { PatientContext } from 'contexts/PatientContext';
import { getYearsArray } from 'utils/functions';
import { useParams } from 'react-router-dom';
import { showError } from 'utils/alerts';
import { toast } from 'react-toastify';
import { editPatient, newPatient } from 'services/patients';
import { uploadFile } from 'services/files';
import { INTITUTION_TYPES, sex_OPTIONS } from 'utils/constants';
import { useAuth } from 'contexts/AuthContext';

interface DemographyPageProps { }
interface IFormInputs {
	identification: string;
	birthYear: number;
	birthDate: Date;
	age: string;
	gender: string;
	ecog: string;
	// consentFile: any;
	app: string;
}

export const Demography: React.FunctionComponent<DemographyPageProps> = () => {
	const { user } = useAuth();
	const isBioBancoInstitution = user.doctor.institution.type === INTITUTION_TYPES['BIOBANK'];
	const { isAdmin } = user;
	const { t } = useTranslation();
	const { patientId } = useParams();
	const navigate = useNavigate();
	const { patient, setPatient, fetching, fetchPatient } =
		useContext<any>(PatientContext);


	const schema = yup
		.object({
			identification: yup.string().required(t('error.requiredField')),
			// birthYear: yup.number().required(t('error.mustChooseAnOption')),
			birthDate: isBioBancoInstitution ? yup.string() : yup.date().required(t('error.mustChooseADate')),
			gender: yup.string().required(t('error.mustChooseAnOption')),
			ecog: yup.string().nullable(),
			app: yup.string().nullable(),
		})
		.required();

	const {
		handleSubmit,
		control,
		reset,
		setValue,
		setError,
		clearErrors,
		formState: { errors, dirtyFields, isValid },
	} = useForm<IFormInputs>({
		resolver: yupResolver(schema),
		mode: 'onChange',
	});

	// const setValues = () => {
	//   let value = patient?.demography?.file?.name || patient?.demography?.consentFile?.name;
	//   if (value) {
	//     setValue('consentFile', value);
	//   }
	// };

	const newFile = async (f: any) => {
		try {
			const formData = new FormData();
			formData.append('file', f);
			const file = await uploadFile(formData);
			return file;
		} catch (error) {
			throw error;
		}
	};

	const handleOnSubmit: SubmitHandler<IFormInputs> = async (data: any) => {
		try {
			const modifiedFields = Object.keys(dirtyFields);
			if (modifiedFields.length > 0) {
				console.log({ modifiedFields, data });
				let fileId: any = undefined;
				// if (modifiedFields.includes('consentFile') && data.consentFile.length > 0) {
				//   const res = await newFile(data.consentFile[0]);
				//   fileId = res.data.id;
				// }
				if (patientId && modifiedFields.length > 0) {
					let updateData: any = {};
					modifiedFields.map((fieldName: string) => {
						let value = data[fieldName];
						if (fieldName === 'identification') {
							updateData = { ...updateData, [fieldName]: value };
						} /* else if (fieldName === 'consentFile') {
              updateData = { ...updateData, demography: { ...updateData.demography, consentFileId: fileId } };
            } */ else {
							updateData = {
								...updateData,
								demography: { ...updateData.demography, [fieldName]: value },
							};
						}
					});
					await editPatient(patientId, updateData);
					setPatient({ ...patient, ...updateData });
					await fetchPatient();
				} else {
					const dataFormatted = {
						identification: data.identification,
						demography: {
							// birthYear: data.birthYear,
							birthDate: isBioBancoInstitution ? new Date() : data.birthDate,
							gender: data.gender,
							ecog: data.ecog,
							// consentFileId: fileId,
							app: data.app,
						},
					};
					const res = await newPatient({ ...dataFormatted });
					console.log({ res });
					toast.success(t('toast.patientCreated'));
					setPatient({ ...res.data.data });

					navigate(`/edit-patient/${res.data.data.id}/samples`);
				}
			}
			// navigate(`/edit-patient/${patientId}/samples`);
			// navigate(`../samples`, {replace: true});
		} catch (error: any) {
			if (error?.response?.status === 409) {
				clearErrors();
				setError('identification', {
					type: 'manual',
					message: t('error.patientCodeAlreadyExist'),
				});
			}
			showError(t('error.savePatientInfo'));
			throw error;
		}
	};

	return (
		<NewPatientStep
			title={`01.- ${t('demography')}`}
			handleOnClickNext={handleSubmit(handleOnSubmit)}
		>
			{fetching ? (
				<Loading />
			) : (
				<form
					onSubmit={handleSubmit(handleOnSubmit)}
					className='grid lg:grid-cols-4 grid-cols-1 gap-6'
				>
					<InputControlled
						disabled={isAdmin}
						name='identification'
						control={control}
						className='lg:col-span-2'
						labelClass='sm'
						label={t('patientCode')}
						defaultValue={patient?.identification}
						//@ts-ignore
						errors={errors.identification}
					/>
					<InputControlled
						disabled={isAdmin}
						className='lg:col-span-2'
						labelClass='sm'
						type='date'
						label={t('birthDate')}
						//@ts-ignore
						errors={errors.birthDate}
						control={control}
						defaultValue={patient?.demography?.birthDate || ''}
						name='birthDate'
					/>
					{/* <SelectControlled
						disabled={isAdmin}
						name='birthYear'
						control={control}
						className='lg:col-span-2'
						labelClass='sm'
						label={t('birthYear')}
						defaultValue={
							patient?.demography?.birthYear
								? {
										value: patient?.demography?.birthYear,
										label: patient?.demography?.birthYear,
								  }
								: undefined
						}
						placeholder={t('select')}
						options={getYearsArray()}
						//@ts-ignore
						errors={errors.birthYear}
					/> */}
					<SelectControlled
						disabled={isAdmin}
						name='gender'
						control={control}
						className='lg:col-span-2'
						labelClass='sm'
						label={t('gender')}
						defaultValue={
							patient?.demography?.gender
								? {
									value: patient?.demography?.gender,
									label: t(patient?.demography?.gender),
								}
								: undefined
						}
						placeholder={t('select')}
						options={sex_OPTIONS}
						//@ts-ignore
						errors={errors.gender}
					/>
					<InputControlled
						disabled={isAdmin}
						name='ecog'
						control={control}
						className='lg:col-span-2'
						labelClass='sm'
						label={t('ecog')}
						defaultValue={patient?.demography?.ecog}
						//@ts-ignore
						errors={errors.ecog}
					/>
					{/* <FileInput className='lg:col-span-4' label={t('consent')} name='consentFile' control={control} /> */}
					<InputControlled
						disabled={isAdmin}
						name='app'
						control={control}
						className='lg:col-span-4'
						labelClass='sm'
						defaultValue={patient?.demography?.app}
						type='textarea'
						label={t('app')}
						//@ts-ignore
						errors={errors.app}
					/>
				</form>
			)}
		</NewPatientStep>
	);
};
